/* eslint react-hooks/exhaustive-deps: "warn" */
import { useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './InsuranceRegistries.module.scss';

type Translate = (key: string) => string;

const getRegistries = (t: Translate) => [
  { flag: '🇬🇧', name: 'Companies House', country: t('United Kingdom') },
  { flag: '🇮🇪', name: 'Companies Registration Office', country: t('Ireland') },
  { flag: '🇫🇷', name: 'Registre du commerce et des sociétés', country: t('France') },
  { flag: '🇩🇪', name: 'Handelsregister', country: t('Germany') },
  { flag: '🇳🇱', name: 'KVK Handelsregister', country: t('Netherlands') },
  { flag: '🇧🇪', name: 'Banque-Carrefour des Entreprises', country: t('Belgium') },
  { flag: '🇱🇺', name: 'Registre de Commerce et des Sociétés', country: t('Luxembourg') },
  { flag: '🇪🇸', name: 'Registro Mercantil', country: t('Spain') },
  { flag: '🇮🇹', name: 'Registro Imprese', country: t('Italy') },
  { flag: '🇵🇹', name: 'Registo Comercial', country: t('Portugal') },
  { flag: '🇦🇹', name: 'Firmenbuch', country: t('Austria') },
  { flag: '🇨🇭', name: 'Zefix · Handelsregister', country: t('Switzerland') },
  { flag: '🇵🇱', name: 'Krajowy Rejestr Sądowy', country: t('Poland') },
  { flag: '🇸🇪', name: 'Bolagsverket', country: t('Sweden') },
  { flag: '🇳🇴', name: 'Brønnøysundregistrene', country: t('Norway') },
  { flag: '🇩🇰', name: 'Det Centrale Virksomhedsregister', country: t('Denmark') },
  { flag: '🇫🇮', name: 'Patentti- ja rekisterihallitus', country: t('Finland') },
  { flag: '🇨🇾', name: 'Registrar of Companies', country: t('Cyprus') },
  { flag: '🇺🇸', name: 'Secretaries of State · SEC EDGAR', country: t('United States') },
  { flag: '🇨🇦', name: 'Corporations Canada', country: t('Canada') },
  { flag: '🇸🇬', name: 'ACRA', country: t('Singapore') },
  { flag: '🇭🇰', name: 'Companies Registry', country: t('Hong Kong') },
  { flag: '🇦🇺', name: 'ASIC', country: t('Australia') },
  { flag: '🇮🇳', name: 'Ministry of Corporate Affairs', country: t('India') },
];

export const InsuranceRegistries = () => {
  const { t } = useInt();
  const ref = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(ref, { threshold: 0.08 });

  const registries = getRegistries(t);

  return (
    <Section background="#F7F9FD" className={styles.root}>
      <div className={styles.head}>
        <TitleBlock
          title={t('400+ registries. Here are twenty-four of them.')}
          description={t(
            'Every record on this page comes from a named government source. These are the offices behind the European and international commercial book.',
          )}
        />
      </div>

      <div className={cn(styles.grid, revealed && styles.gridIn)} ref={ref}>
        {registries.map((registry, i) => (
          <div className={styles.item} style={{ transitionDelay: `${(i % 8) * 35}ms` }} key={registry.name}>
            <span className={styles.flagBadge}>
              <span className={styles.flag}>{registry.flag}</span>
            </span>
            <span className={styles.info}>
              <b>{registry.name}</b>
              <span>{registry.country}</span>
            </span>
          </div>
        ))}
      </div>

      <div className={styles.foot}>
        <span className={styles.footNote}>
          {t('Twenty-four shown. The full list runs to 400+ across 200+ countries.')}
        </span>
        <Button type="dark-outline" size="lg" route="/data-dictionary">
          {t('See the full coverage list')}
        </Button>
      </div>
    </Section>
  );
};
